/**
 * "Fasilitointi" for one chain module in /admin/workshop and Käsikirjoitus
 * (#1082, specs/facilitation-modules.md §3): this Kuutio's customizations of
 * the module's facilitation content. The base shows as each field's
 * placeholder; text typed here becomes a sparse override in the step's
 * `module_config.facilitation`, saved on blur through
 * API_ENDPOINTS.INSTANCE_CHAIN_STEP, and a reset drops it back to the base.
 * The run-of-play is customized as a whole list (FacilitationStepsEditor), and
 * the AI chat (#1174, FacilitationChat) proposes texts that save the same way
 * the moment they are accepted. Overrides never change the base, which admins
 * refine for every Kuutio in Ylläpito → Fasilitointi (#1091).
 */
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, RotateCcw, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../../config/api';
import FacilitationChat from '../FacilitationChat/FacilitationChat';
import FacilitationStepsEditor from './FacilitationStepsEditor';

const FIELDS = ['what', 'why', 'intro', 'how', 'technicalSetup', 'notes'];
const BEFORE_STEPS = ['what', 'why', 'intro', 'how', 'technicalSetup'];

const Wrap = styled.div`
  margin: 0 0 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
`;

const Toggle = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 0 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  .count { font-weight: 400; color: rgba(134, 239, 172, 0.9); font-size: 12px; }
  svg { transition: transform 0.2s; transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')}); }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 16px 16px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  .head { display: flex; align-items: center; gap: 8px; }
  .mark { color: rgba(134, 239, 172, 0.9); }
  textarea {
    min-height: 72px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid ${(p) => (p.$customized ? 'rgba(134, 239, 172, 0.45)' : 'rgba(255, 255, 255, 0.15)')};
    background: rgba(0, 0, 0, 0.25);
    color: #fff;
    font: inherit;
    font-size: 14px;
    line-height: 1.45;
    resize: vertical;
    &::placeholder { color: rgba(255, 255, 255, 0.35); }
  }
`;

const Reset = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
`;

const ChatToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(14, 94, 99, 0.8);
  background: ${(p) => (p.$open ? 'rgba(14, 94, 99, 0.35)' : 'transparent')};
  color: #fff;
  font-size: 13px;
  cursor: pointer;
`;

const Note = styled.p`
  margin: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const Status = styled.span`
  font-size: 13px;
  color: ${(p) => (p.$error ? '#f87171' : 'rgba(134, 239, 172, 0.9)')};
`;

/**
 * @param {{ step: object, instanceId?: string|null, locked?: boolean, title?: string,
 *   onSave: (patch: object) => Promise<unknown> }} props
 *   `step.facilitation` is the merged view from the chain endpoint (resolveFacilitation);
 *   `title` replaces the panel's heading (the Käsikirjoitus view names it as an edit);
 *   `instanceId` enables the AI chat, which needs the step's route.
 */
export default function FacilitationPanel({ step, instanceId = null, locked = false, title = null, onSave }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [stepsDraft, setStepsDraft] = useState(null);
  const [status, setStatus] = useState(null);
  const merged = step.facilitation || { base: {}, customized: {} };
  const own = step.moduleConfig?.facilitation || {};

  useEffect(() => {
    setDraft(Object.fromEntries(FIELDS.map((f) => [f, typeof own[f] === 'string' ? own[f] : ''])));
    setStepsDraft(Array.isArray(own.steps) ? own.steps : null);
    // own is derived from step.moduleConfig; the step id and its config identify a fresh read.
  }, [step.stepId, step.moduleConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Write the whole sparse override; the server replaces module_config.facilitation as one. */
  const persist = async (next) => {
    const { facilitation: _drop, ...rest } = step.moduleConfig || {};
    const moduleConfig = Object.keys(next).length ? { ...rest, facilitation: next } : rest;
    try {
      await onSave({ moduleConfig });
      setStatus({ text: t('workshopOrchestrator.saved') });
    } catch (err) {
      setStatus({ text: t('workshopOrchestrator.saveFailed', { error: err.message }), error: true });
    }
  };

  const saveField = (field, value) => {
    const next = { ...own };
    const text = (value || '').trim();
    if (text) next[field] = text; else delete next[field];
    if ((own[field] || '') === text) return;
    return persist(next);
  };

  const saveSteps = (list) => {
    const next = { ...own };
    if (Array.isArray(list)) next.steps = list; else delete next.steps;
    if (JSON.stringify(own.steps ?? null) === JSON.stringify(list ?? null)) return;
    return persist(next);
  };

  // A proposal equal to the base is a reset, not an override identical to it.
  const acceptText = (field, text) => {
    const value = text === (merged.base?.[field] || '') ? '' : text;
    setDraft((d) => ({ ...d, [field]: value }));
    saveField(field, value);
  };
  const acceptSteps = (steps) => {
    setStepsDraft(steps);
    saveSteps(steps);
  };

  // What the AI sees: the effective text, and which of it is this Kuutio's own.
  const chatDraft = useMemo(() => ({
    ...Object.fromEntries(FIELDS.map((f) => [f, (draft[f] || '').trim() || merged.base?.[f] || ''])),
    steps: stepsDraft ?? merged.base?.steps ?? [],
    customized: {
      ...Object.fromEntries(FIELDS.map((f) => [f, Boolean((draft[f] || '').trim())])),
      steps: Array.isArray(stepsDraft),
    },
  }), [draft, stepsDraft, merged.base]);

  const customizedCount = [...FIELDS, 'steps'].filter((f) => merged.customized?.[f]).length;

  const renderField = (field) => (
    <Field key={field} $customized={Boolean(merged.customized?.[field])}>
      <span className="head">
        {t(`facilitationPanel.${field}`)}
        {merged.customized?.[field] && <span className="mark">● {t('facilitationPanel.customized')}</span>}
        {merged.customized?.[field] && !locked && (
          <Reset type="button" onClick={(e) => { e.preventDefault(); setDraft((d) => ({ ...d, [field]: '' })); saveField(field, ''); }}>
            <RotateCcw size={12} />{t('facilitationPanel.reset')}
          </Reset>
        )}
      </span>
      <textarea disabled={locked} value={draft[field] ?? ''}
        placeholder={merged.base?.[field] || t('facilitationPanel.noBase')}
        onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
        onBlur={(e) => saveField(field, e.target.value)} />
    </Field>
  );

  return (
    <Wrap>
      <Toggle type="button" $open={open} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>
          {title || t('facilitationPanel.title')}
          {customizedCount > 0 && <span className="count"> · {t('facilitationPanel.customizedCount', { count: customizedCount })}</span>}
        </span>
        <ChevronDown size={16} />
      </Toggle>
      {open && (
        <Body>
          <Note>{t('facilitationPanel.hint')}</Note>
          {BEFORE_STEPS.map(renderField)}
          <FacilitationStepsEditor
            baseSteps={merged.base?.steps || []}
            ownSteps={stepsDraft}
            locked={locked}
            t={t}
            onChange={acceptSteps}
          />
          {renderField('notes')}
          {!locked && instanceId && (
            <ChatToggle type="button" $open={chatOpen} onClick={() => setChatOpen((o) => !o)} aria-expanded={chatOpen}>
              <Sparkles size={14} />{chatOpen ? t('facilitationChat.close') : t('facilitationChat.title')}
            </ChatToggle>
          )}
          {chatOpen && instanceId && (
            <FacilitationChat
              key={step.stepId}
              endpoint={API_ENDPOINTS.CHAIN_STEP_FACILITATION_CHAT(instanceId, step.stepId)}
              fields={FIELDS.map((f) => ({ key: f, label: t(`facilitationPanel.${f}`) }))}
              draft={chatDraft}
              hint={t('facilitationChat.hintInstance')}
              onAccept={acceptText}
              onAcceptSteps={acceptSteps}
              disabled={locked}
            />
          )}
          {status && <Status $error={status.error}>{status.text}</Status>}
        </Body>
      )}
    </Wrap>
  );
}
